import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { ModalShell, ModalActions, useDialogs } from '../../components/PromptDialog';
import { createRule, deleteRule, fetchRules, patchRule, socialKeys } from './api';
import { ChannelBar, kanalName, reichweiteText, tagKurz } from './bausteine';
import { useCanWrite } from '../auth/permissions';
import { PUBLISH_CHANNELS, type Channel } from './types';

const t = darkTheme.colors;

const WOCHENTAGE = [
  { nr: 1, kurz: 'Mo' }, { nr: 2, kurz: 'Di' }, { nr: 3, kurz: 'Mi' },
  { nr: 4, kurz: 'Do' }, { nr: 5, kurz: 'Fr' }, { nr: 6, kurz: 'Sa' }, { nr: 7, kurz: 'So' },
];

const STREAMS = [
  'MODERATIONS_SHORT', 'ERKLAERVIDEO', 'PRODUKTVIDEO',
  'AUDIO_LEKTION', 'BELEG_KARTE', 'KARTE', 'AD',
];

/**
 * Rhythmen (CUR-332).
 *
 * **Ein Rhythmus legt keine Termine an.** Er erzeugt beim Lesen freie Slots;
 * erst die Zuweisung eines Assets macht daraus einen echten Termin. Deshalb ist
 * das Löschen einer Regel hier gefahrlos: Was schon zugewiesen ist, bleibt.
 *
 * **Empfehlung für den Start** (aus der Spec, an der Git-Historie gemessen):
 * nicht mit zwei Erklärvideo-Tagen anfangen — mit dem heutigen Bestand trägt
 * Di+Do nur zwei Wochen. Erst je ein Tag pro Serie, zweiter Tag, sobald die
 * Reichweite über sechs Wochen steht.
 */
export function RhythmenPage(): JSX.Element {
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const [neu, setNeu] = useState(false);
  // Nur-Lesen-Rolle sieht den Plan, legt aber keine Rhythmen an, pausiert
  // keine und loescht keine (CUR-995).
  const canWrite = useCanWrite();

  const rules = useQuery({ queryKey: socialKeys.rules(), queryFn: fetchRules });

  const aendern = useMutation({
    mutationFn: (v: { id: string; patch: Parameters<typeof patchRule>[1] }) => patchRule(v.id, v.patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['social'] }),
  });
  const loeschen = useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['social'] }),
  });

  return (
    <div className="mx-auto max-w-[1000px] p-4 lg:p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: t.textPrimary }}>Rhythmen</h1>
          <p className="text-sm" style={{ color: t.textTertiary }}>
            Feste Wochentage je Serie — und wie lange der Vorrat sie trägt.
          </p>
        </div>
        {canWrite && (
          <button type="button" onClick={() => setNeu(true)}
            className="min-h-[44px] rounded-lg px-4 font-semibold text-white" style={{ backgroundColor: t.accent }}>
            Rhythmus anlegen
          </button>
        )}
      </header>

      <p className="mb-4 rounded-md border p-3 text-sm"
        style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface, color: t.textSecondary }}>
        Ein <strong>leerer wiederkehrender Slot</strong> sagt nach außen lauter „hier hat jemand
        aufgehört" als eine unregelmäßige Veröffentlichung, die nichts verspricht. Die Zahl, auf
        die es beim Planen ankommt, ist deshalb nicht der Rhythmus, sondern seine Reichweite.
      </p>

      {rules.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-md" style={{ backgroundColor: t.bgSurface }} />
          ))}
        </div>
      )}

      {rules.data && rules.data.rules.length === 0 && (
        <div className="rounded-md border p-6 text-sm"
          style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface, color: t.textSecondary }}>
          Noch kein Rhythmus angelegt. Ohne einen zeigt der Kalender nur, was schon terminiert ist —
          nicht, wann wieder Sendetag wäre.
        </div>
      )}

      <ul className="space-y-2">
        {(rules.data?.rules ?? []).map((r) => (
          <li key={r.id} className="rounded-md border p-3"
            style={{
              borderColor: r.knapp ? t.warning : t.borderSubtle,
              backgroundColor: t.bgSurface,
              opacity: r.active ? 1 : 0.55,
            }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold" style={{ color: t.textPrimary }}>{r.stream}</span>
                <ChannelBar channel={r.channel} />
                <span className="text-sm" style={{ color: t.textSecondary }}>
                  {r.weekdays.map((w) => WOCHENTAGE.find((x) => x.nr === w)?.kurz).join(' · ')}
                </span>
                <span className="text-xs" style={{ color: t.textTertiary }}>
                  ab {tagKurz(r.startsOn)}{r.endsOn ? ` bis ${tagKurz(r.endsOn)}` : ''}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm" style={{ color: t.textTertiary }}>{r.vorrat} Folgen im Vorrat</span>
                <span className="text-sm font-semibold" style={{ color: r.knapp ? t.warning : t.textSecondary }}>
                  {r.knapp ? '⚠ ' : ''}{reichweiteText(r.reichweite)}
                </span>
                {canWrite && (
                  <>
                    <button type="button"
                      onClick={() => aendern.mutate({ id: r.id, patch: { active: !r.active } })}
                      className="min-h-[44px] rounded-md px-3 text-xs font-semibold"
                      style={{ backgroundColor: t.bgElevated, color: t.textSecondary }}>
                      {r.active ? 'Pausieren' : 'Wieder aktiv'}
                    </button>
                    <button type="button"
                      onClick={async () => {
                        const ok = await dialogs.confirm({
                          title: 'Rhythmus löschen',
                          message:
                            'Bereits zugewiesene Termine bleiben bestehen — die Regel erzeugt sie nur beim Lesen. ' +
                            'Es verschwinden also nur die freien Slots.',
                          confirmLabel: 'Löschen',
                          destructive: true,
                        });
                        if (ok) loeschen.mutate(r.id);
                      }}
                      className="min-h-[44px] rounded-md px-3 text-xs" style={{ color: t.textTertiary }}>
                      Löschen
                    </button>
                  </>
                )}
              </div>
            </div>
            {r.knapp && (
              <p className="mt-2 text-xs" style={{ color: t.warning }}>
                Unter drei Wochen Reichweite — Zeit für einen Produktionstag, bevor die Lücke entsteht.
              </p>
            )}
          </li>
        ))}
      </ul>

      {neu && <NeuModal onClose={() => setNeu(false)} />}
      {dialogs.dialog}
    </div>
  );
}

function NeuModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [stream, setStream] = useState(STREAMS[0] ?? 'MODERATIONS_SHORT');
  const [kanal, setKanal] = useState<Channel>('YOUTUBE');
  const [tage, setTage] = useState<number[]>([1]);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [fehler, setFehler] = useState<string | null>(null);

  const anlegen = useMutation({
    mutationFn: () => createRule({ stream, channel: kanal, weekdays: tage, startsOn: start }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['social'] });
      onClose();
    },
    onError: (e: Error) => setFehler(e.message),
  });

  return (
    <ModalShell
      title="Rhythmus anlegen"
      onSubmit={(e) => {
        e.preventDefault();
        if (tage.length === 0) return;
        anlegen.mutate();
      }}
    >
      <label className="mb-3 block text-sm" style={{ color: t.textSecondary }}>
        Serie
        <select value={stream} onChange={(e) => setStream(e.target.value)}
          className="mt-1 block min-h-[44px] w-full rounded-md px-3"
          style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
          {STREAMS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label className="mb-3 block text-sm" style={{ color: t.textSecondary }}>
        Kanal
        <select value={kanal} onChange={(e) => setKanal(e.target.value as Channel)}
          className="mt-1 block min-h-[44px] w-full rounded-md px-3"
          style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
          {PUBLISH_CHANNELS.map((c) => <option key={c} value={c}>{kanalName(c)}</option>)}
        </select>
      </label>
      <fieldset className="mb-3">
        <legend className="mb-1 text-sm" style={{ color: t.textSecondary }}>Wochentage</legend>
        <div className="flex flex-wrap gap-1">
          {WOCHENTAGE.map((w) => {
            const an = tage.includes(w.nr);
            return (
              <button key={w.nr} type="button"
                onClick={() => setTage(an ? tage.filter((x) => x !== w.nr) : [...tage, w.nr].sort((a, b) => a - b))}
                aria-pressed={an}
                className="min-h-[44px] min-w-[44px] rounded-md text-sm font-semibold"
                style={{
                  backgroundColor: an ? t.accent : t.bgElevated,
                  color: an ? '#fff' : t.textSecondary,
                }}>
                {w.kurz}
              </button>
            );
          })}
        </div>
      </fieldset>
      <label className="mb-3 block text-sm" style={{ color: t.textSecondary }}>
        Ab
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
          className="mt-1 block min-h-[44px] w-full rounded-md px-3"
          style={{ backgroundColor: t.bgElevated, color: t.textPrimary }} />
      </label>
      {fehler && <p className="mb-3 text-sm" style={{ color: t.danger }}>{fehler}</p>}
      <ModalActions onCancel={onClose} confirmLabel="Anlegen" disabled={tage.length === 0 || anlegen.isPending} />
    </ModalShell>
  );
}
